<template>
<div class="movie-player-container">
  <div class="movie-player">
    <div class="top-bar-area mb8" v-if="movieList.isCurrentMovieListBothReady" ref="refTopBarArea">
      <CompareModeTopBar
        v-model:left-movie-list-id="state.left.movieList.listSelection.currentMovieListId"
        v-model:right-movie-list-id="state.right.movieList.listSelection.currentMovieListId"
        :left-movie-lists="state.left.movieList.listSelection.currentMovieLists"
        :right-movie-lists="state.right.movieList.listSelection.currentMovieLists"
        :left-base-date="state.movieSearch.left.baseDate"
        :right-base-date="state.movieSearch.right.baseDate"
        :selected-camera-position="state.ui.selectedCameraPosition"
        @change-movie-list-id="onCurrentMovieListIdChange"
        @search-by-date-range="searchMoviesByDateRange"
        @change-camera-position="camera.changeCameraPosition"
        @switch-to-full-mode="switchToFullMode"
        @close="closeMoviePlayer" />
    </div>
    <div class="video-area" ref="refVideoArea">
      <CompareModeVideos
        :video-area-width="styles.videoAreaWidth"
        :video-area-height="styles.videoAreaHeight"
        :vid-left="playback.vid1"
        :vid-right="playback.vid2"
        :active-vjs-lr-map="state.ui.activeVjsLrMap"
        :is-current-movie-ready="{ left: state.left.isMovieReady, right: state.right.isMovieReady }"
        :movie-search="state.movieSearch"
        :is-downloading-screenshot="download.isDownloadingScreenshot"
        :is-downloading-movie-file="download.isDownloadingMovieFile"
        @enter-fullscreen="camera.enterFullScreen"
        @take-screenshot="download.takeScreenshot"
        @download-movie-file="download.tryDownload"
        @toolbox-area-click="onVideoToolboxAreaClicked" />
    </div>
    <div class="info-area mt16"
        :style="{ height: styles.infoHeight }">
      <div class="map-area">
        <ExtremeMap ref="refExtremeMap"
          :map-essentials="extremeMapEssentials"
          :disable-movie-layer-interaction="true"
          @click-map="map.seekByMapClick"
          @click-item="map.onMapItemClicked"
          @all-deselected="map.onMapAllDeselected" />
      </div>
      <div class="control-area ml8">
        <div class="control-area-toolbar">
          <div>
            <button class="btn btn-secondary btn-sm mb4"
                @click="map.alignLocationToLeftMovie">
              <i class="fas fa-angle-double-left"></i>
              走行地点を左に合わせる
            </button>
          </div>
          <div>
            <div class="btn-group btn-group-toggle ws-nowrap" style="margin-top:-1px;">
              <label class="btn btn-info btn-sm" v-for="(e, i) in [
                  { bulkControl: true, disp: '同時操作' },
                  { bulkControl: false, disp: '個別操作' },
                ]" :class="{active: state.ui.bulkControl === e.bulkControl}" :key="i">
                <input type="radio" name="options"
                :checked="state.ui.bulkControl === e.bulkControl"
                autocomplete="off" @click="state.ui.bulkControl = e.bulkControl">{{e.disp}}
              </label>
            </div>
          </div>
          <div>
            <button class="btn btn-secondary btn-sm"
                @click="map.alignLocationToRightMovie">
              走行地点を右に合わせる
              <i class="fas fa-angle-double-right"></i>
            </button>
          </div>
        </div>
        <div class="controls-container">
          <div class="row1" v-show="state.ui.bulkControl">
            <MoviePlayerControlArea ref="refControlAreaBoth"
              control-area-id="both"
              view-mode="other"
              size-mode="lg"
              :play-speed="state.left.playback.currentPlaySpeed"
              :is-play-backward="state.both.isPlayBackward"
              :is-playing="state.both.isPlaying"
              @play="playback.playUI('both')"
              @pause="playback.pauseUI('both')"
              @tick-backward="playback.tickMovieBackward('both')"
              @tick-forward="playback.tickMovieForward('both')"
              @set-play-speed="playback.setPlaySpeed('both', $event)"
              @set-volume="playback.setVolume('both', $event)"
              @playmode-change="playback.playModeChange('both', $event)" />
          </div>
          <div class="row2" v-show="!state.ui.bulkControl">
            <MoviePlayerControlArea ref="refControlAreaLeft"
              control-area-id="left"
              view-mode="comp"
              :size-mode="sizes.control"
              :play-speed="state.left.playback.currentPlaySpeed"
              :is-play-backward="state.left.playback.isPlayBackward"
              :is-playing="state.left.playback.isPlaying"
              :current-time-disp="movieList.playerControlCurrentTimeDispLeft"
              :start-time-disp="state.left.movieList.timeDisp.startTimeDisp"
              :end-time-disp="state.left.movieList.timeDisp.endTimeDisp"
              :seek-min="state.left.playback.seek.min"
              :seek-max="state.left.playback.seek.max"
              :seek-current="state.left.playback.seek.current"
              :seek-step="state.left.playback.seek.step"
              @play="playback.playMovie('left')"
              @pause="playback.pauseMovie('left')"
              @seek-change="seek.seekBySeekBar('left', $event)"
              @tick-backward="playback.tickMovieBackward('left')"
              @tick-forward="playback.tickMovieForward('left')"
              @play-previous-movie="playback.playPreviousMovie('left')"
              @play-next-movie="playback.playNextMovie('left')"
              @set-play-speed="playback.setPlaySpeed('left', $event)"
              @set-volume="playback.setVolume('left', $event)"
              @playmode-change="playback.playModeChange('left', $event)" />
            <div class="spacer m4"></div>
            <MoviePlayerControlArea ref="refControlAreaRight"
              control-area-id="right"
              view-mode="comp"
              :size-mode="sizes.control"
              :play-speed="state.right.playback.currentPlaySpeed"
              :is-play-backward="state.right.playback.isPlayBackward"
              :is-playing="state.right.playback.isPlaying"
              :current-time-disp="movieList.playerControlCurrentTimeDispRight"
              :start-time-disp="state.right.movieList.timeDisp.startTimeDisp"
              :end-time-disp="state.right.movieList.timeDisp.endTimeDisp"
              :seek-min="state.right.playback.seek.min"
              :seek-max="state.right.playback.seek.max"
              :seek-current="state.right.playback.seek.current"
              :seek-step="state.right.playback.seek.step"
              :corner-label-opacity="0.5"
              @play="playback.playMovie('right')"
              @pause="playback.pauseMovie('right')"
              @seek-change="seek.seekBySeekBar('right', $event)"
              @tick-backward="playback.tickMovieBackward('right')"
              @tick-forward="playback.tickMovieForward('right')"
              @play-previous-movie="playback.playPreviousMovie('right')"
              @play-next-movie="playback.playNextMovie('right')"
              @set-play-speed="playback.setPlaySpeed('right', $event)"
              @set-volume="playback.setVolume('right', $event)"
              @playmode-change="playback.playModeChange('right', $event)" />
          </div>
        </div>
        <div class="mt8 metas-container">
          <MovieMetaInfoArea ref="refMetaInfoAreaLeft"
            v-if="movieList.isCurrentMovieListLeftReady"
            view-mode="comp"
            :movie="state.left.movieList.current.currentMovie"
            :movie-geo-index="state.left.geo.currentMgi"
            :movie-current-dt="state.left.geo.currentMovieCurrentDt"
            :enable-sensor-chart="true"
            :enable-time-seek="true"
            :location-disp="state.left.geo.locationDisp"
            @try-show-new-sensor-chart="sensor.getCurrentMovieListSensorData('left')"
            @start-time-edit="seek.startTimeTextEdit()"
            @move-to-time="seek.seekByTimeText('left', $event)"
            @edit-movie-tags="tagModal.startEditMovieTags('left')" />
          <div class="spacer m4"></div>
          <MovieMetaInfoArea ref="refMetaInfoAreaRight"
            v-if="movieList.isCurrentMovieListRightReady"
            view-mode="comp"
            :movie="state.right.movieList.current.currentMovie"
            :movie-geo-index="state.right.geo.currentMgi"
            :movie-current-dt="state.right.geo.currentMovieCurrentDt"
            :enable-sensor-chart="true"
            :enable-time-seek="true"
            :corner-label-opacity="0.5"
            :location-disp="state.right.geo.locationDisp"
            @try-show-new-sensor-chart="sensor.getCurrentMovieListSensorData('right')"
            @start-time-edit="seek.startTimeTextEdit()"
            @move-to-time="seek.seekByTimeText('right', $event)"
            @edit-movie-tags="tagModal.startEditMovieTags('right')" />
        </div>
        <div class="mt8 map-elem-infos-container">
          <MapElemPinCreator
            :map-elem-info="state.left.geo.mapElemInfo"
            :map-elem-icon-path="map.mapElemIconPath"
            :visible-geo-item-layers="map.visibleGeoItemLayers"
            :current-mgi="state.left.geo.currentMgi"
            :create-failed="state.left.geo.createMapElemFailed"
            @start-create="map.startCreateMapElem('left', playback.vid1)"
            @create="map.createMapElem('left', $event)"
            @cancel-edit="map.clearMapElemInfo('left')" />
          <div class="spacer m4"></div>
          <MapElemPinCreator
            :map-elem-info="state.right.geo.mapElemInfo"
            :map-elem-icon-path="map.mapElemIconPath"
            :visible-geo-item-layers="map.visibleGeoItemLayers"
            :current-mgi="state.right.geo.currentMgi"
            :create-failed="state.right.geo.createMapElemFailed"
            @start-create="map.startCreateMapElem('right', playback.vid2)"
            @create="map.createMapElem('right', $event)"
            @cancel-edit="map.clearMapElemInfo('right')" />
        </div>
        <div class="ifd-link-area-container" v-if="!!ifdLinkPrefix">
          <InfraDoctorLinkArea
            :ifd-link-prefix="ifdLinkPrefix"
            :current-movie="state.left.movieList.current.currentMovie"
            :current-movie-list="state.left.movieList.listSelection.currentMovieList"
            :current-mgi="state.left.geo.currentMgi"
            :current-movie-idx="state.left.movieList.current.currentMovieIdx"
            :current-mgi-idx="state.left.geo.currentMgiIdx"
            :is-first-movie="state.left.movieList.current.isFirstMovie"
            :is-last-movie="state.left.movieList.current.isLastMovie"
            :is-ready="state.left.isMovieReady" />
          <div class="spacer m4"></div>
          <InfraDoctorLinkArea
            :ifd-link-prefix="ifdLinkPrefix"
            :current-movie="state.right.movieList.current.currentMovie"
            :current-movie-list="state.right.movieList.listSelection.currentMovieList"
            :current-mgi="state.right.geo.currentMgi"
            :current-movie-idx="state.right.movieList.current.currentMovieIdx"
            :current-mgi-idx="state.right.geo.currentMgiIdx"
            :is-first-movie="state.right.movieList.current.isFirstMovie"
            :is-last-movie="state.right.movieList.current.isLastMovie"
            :is-ready="state.right.isMovieReady" />
        </div>
      </div>
    </div>
  </div>
  <div class="movie-player-backdrop">
  </div>

  <TagModal v-if="tagModal.movieTagModal.show" class="z-index-3000"
    title="動画タグの編集"
    v-model="tagModal.movieTagModal.tags"
    @close="tagModal.updateMovieTags"
    @dismiss="tagModal.movieTagModal.show = false" />
</div>
</template>

<script setup lang="ts">
import MoviePlayerControlArea from 'src/components/lib/MoviePlayerControlArea/index.vue';
import MovieMetaInfoArea from 'src/components/lib/MovieMetaInfoArea/index.vue';
import InfraDoctorLinkArea from 'src/components/lib/MoviePlayerCompareMode/InfraDoctorLinkArea.vue';
import MapElemPinCreator from 'src/components/lib/MoviePlayerCompareMode/MapElemPinCreator.vue';
import CompareModeTopBar from 'src/components/lib/MoviePlayerCompareMode/CompareModeTopBar.vue';
import CompareModeVideos from 'src/components/lib/MoviePlayerCompareMode/CompareModeVideos.vue';
import { initGeoItemsMeta } from 'src/models/geoItem';
import { initMovieLayerMeta } from 'src/models';
import type { CompareModeProps, CompareModeEmit } from 'src/models/moviePlayer';
import { computed } from 'vue';
import { useAuth } from 'src/composables/useAuth';
import {
  useMoviePlayerCompareModeState,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeState';
import {
  useMoviePlayerCompareModePlayback,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModePlayback';
import {
  useMoviePlayerCompareModeSeek,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeSeek';
import {
  useMoviePlayerCompareModeCamera,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeCamera';
import {
  useMoviePlayerCompareMode,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareMode';
import {
  useMoviePlayerCompareModeDownload,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeDownload';
import {
  useMoviePlayerCompareModeSensor,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeSensor';
import {
  useMoviePlayerCompareModeTagModal,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeTagModal';
import {
  useMoviePlayerCompareModeMapLinkage,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeMapLinkage';
import {
  useMoviePlayerCompareModeMovieList,
} from 'src/components/lib/MoviePlayerCompareMode/composables/useMoviePlayerCompareModeMovieList';

const props = withDefaults(defineProps<CompareModeProps>(), {
  movieLists: () => [],
  movieListId: '',
  parentGeoItemsMeta: initGeoItemsMeta,
  parentMovieLayerMeta: initMovieLayerMeta,
});

const emit = defineEmits<CompareModeEmit>();

const playerState = useMoviePlayerCompareModeState();
const {
  state,
  enqueueUiTask,
  refExtremeMap,
  refTopBarArea,
  refVideoArea,
  refControlAreaBoth,
  refControlAreaLeft,
  refControlAreaRight,
  refMetaInfoAreaLeft,
  refMetaInfoAreaRight,
} = playerState;

// 分割composableの構築順序: 後段は前段の関数をDIで受け取るため、この順序を変更すると未定義参照になる。
const playback = useMoviePlayerCompareModePlayback(state, { enqueueUiTask });
const seek = useMoviePlayerCompareModeSeek(state, {
  setCurrentMovie: playback.setCurrentMovie, pauseUI: playback.pauseUI, playUI: playback.playUI,
});
const camera = useMoviePlayerCompareModeCamera(state, {
  getVjsForLr: playback.getVjsForLr, getUIPlayState: playback.getUIPlayState,
  pauseUI: playback.pauseUI, playUI: playback.playUI,
});
const download = useMoviePlayerCompareModeDownload(state, { getVjsForVid: playback.getVjsForVid });
const sensor = useMoviePlayerCompareModeSensor(state, { refMetaInfoAreaLeft, refMetaInfoAreaRight });
const tagModal = useMoviePlayerCompareModeTagModal(state, { pauseUI: playback.pauseUI });
const movieList = useMoviePlayerCompareModeMovieList(state, { parentMovieSearch: props.parentMovieSearch });
const map = useMoviePlayerCompareModeMapLinkage(state, {
  refExtremeMap,
  parentGeoItemsMeta: props.parentGeoItemsMeta,
  seekMovie: seek.seekMovie,
  pauseUI: playback.pauseUI,
  playUI: playback.playUI,
  getUIPlayState: playback.getUIPlayState,
  getScreenshotBlob: camera.getScreenshotBlob,
  isCurrentMovieListBothReady: () => movieList.isCurrentMovieListBothReady,
});
const auth = useAuth();
const ifdLinkPrefix = computed(() => auth.user?.settings.ifdLink);

const sub = { playback, seek, map, movieList, tagModal };
const {
  onCurrentMovieListIdChange,
  searchMoviesByDateRange,
  closeMoviePlayer,
  onVideoToolboxAreaClicked,
  shortcutKeyAction,
  switchToFullMode,
  sizes,
  styles,
} = useMoviePlayerCompareMode(props, emit, playerState, sub);

defineExpose({
  shortcutKeyAction,
});
</script>

<style lang="scss"  scoped>
.movie-player-container {
  position: absolute;
  top: 0;
  left: 0;
}
.movie-player-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2040;
  width: 100vw;
  height: 100vh;
  background-color: #000000;
  opacity: 0.5;
}
.movie-player {
  position: fixed;
  top: 6px;
  left: 8px;
  bottom: 6px;
  right: 8px;
  z-index: 2050;
  background-color: #ffffff;
  padding: 8px;

  .top-bar-area {
    display: flex;
  }
  .info-area {
    display: flex;
    overflow: scroll;
    .map-area {
      border: 1px solid #dddddd;
      flex-grow: 1;
    }
    .control-area {
      .map-elem-info-area {
        flex-grow: 1;
        border: 1px dashed #7ec3d9;
      }
    }
  }

  .control-area-toolbar {
    display: flex;
    justify-content: space-between;
    padding: 1px 0 0 0;
  }
  .controls-container .row2 {
    display: flex;
  }
  .metas-container {
    display: flex;
    align-items: flex-start;
  }
  .map-elem-infos-container {
    display: flex;
  }
  .ifd-link-area-container {
    display: flex;
  }

  .movie-search-btn-wrap {
    margin-right: 24px;
    .dropdown-item {
      color: #555555;
    }
  }

  @media only screen and (min-width: 0px) {
    .control-area { max-width: 320px; }
    .controls-container .row2 { flex-wrap: wrap; }
    .metas-container { flex-wrap: wrap; }
    .map-elem-infos-container { flex-wrap: wrap; }
    .ifd-link-area-container { flex-wrap: wrap; }
    .movie-player-control-area.compare-mode,
    .movie-meta-info-area.compare-mode,
    .map-elem-info-area.compare-mode,
    .ifd-link-area.compare-mode {
      width: 100%;
    }
  }
  @media only screen and (min-width: 560px) {
    .control-area { max-width: 400px; }
  }
  @media only screen and (min-width: 768px) {
    .control-area { max-width: 100%; }
    .controls-container .row2 { flex-wrap: nowrap; }
    .metas-container { flex-wrap: nowrap; }
    .map-elem-infos-container { flex-wrap: nowrap; }
    .ifd-link-area-container { flex-wrap: nowrap; }
    .movie-player-control-area.compare-mode,
    .movie-meta-info-area.compare-mode,
    .map-elem-info-area.compare-mode,
    .ifd-link-area.compare-mode {
      width: 50%;
    }
  }

  @media only screen and (min-width: 1200px) {
    .movie-player-control-area.compare-mode,
    .movie-meta-info-area.compare-mode,
    .map-elem-info-area.compare-mode {
      max-width: 360px;
    }
  }

  @media only screen and (min-width: 1400px) {
    .movie-player-control-area.compare-mode,
    .movie-meta-info-area.compare-mode,
    .map-elem-info-area.compare-mode {
      max-width: 460px;
    }
  }
}
</style>
